// M2 social components: chats, threads, profiles, and the table for three.
import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import htm from 'htm';
import { drawSprite, drawPlant, petSeedFor, SPRITE_W, SPRITE_H } from './pet.js';
import { funStatusLine, agentLabel } from './fun.js';
import { GardenPage } from './garden.js';

const html = htm.bind(h);

function Pet({ username, cell = 4, stage = 'adult', wins = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) drawSprite(ref.current, petSeedFor(username), { cell, stage, wins });
  }, [username, cell, stage, wins]);
  return html`<canvas ref=${ref} class="pixel" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px" />`;
}

const IMG_RE = /\.(png|jpe?g|gif|webp)$/i;

function MsgRow({ m, onProfile }) {
  return html`<div class="msg">
    <span class="who clickable" onClick=${() => onProfile?.(m.from.username)}>${m.from.username}</span>
    ${m.via_agent && html`<span class="agentbadge">agent</span>`}
    <span class="body">
      ${m.body}
      ${m.attachment_url &&
      (IMG_RE.test(m.attachment_name ?? '')
        ? html`<a href=${m.attachment_url} target="_blank"><img class="chatimg" src=${m.attachment_url} /></a>`
        : html`<a class="filelink" href=${m.attachment_url} target="_blank">📎 ${m.attachment_name}</a>`)}
    </span>
  </div>`;
}

// (chat UI moved to web/src/chat.js — ChatsTab/ThreadView live there now)


// one link = one friend — the server mints fresh codes (20/day); shared by the
// invite card and the empty-search hints, so it lives outside any one component
async function mintInvite(onToast) {
  const r = await fetch('/api/invites', { method: 'POST' });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.url) return onToast?.(d.error ?? 'invite mint failed');
  try {
    await navigator.clipboard.writeText(d.url);
    onToast?.('invite link copied — you both get +5 seeds when they connect an agent 🌱');
  } catch {
    onToast?.(`invite link: ${d.url}`);
  }
}

// find-a-friend: prefix search, one tap to send a card
function UserSearch({ onProfile, reloadSocial, onToast }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);
  const timer = useRef(null);

  const search = (text) => {
    clearTimeout(timer.current);
    if (text.trim().length < 2) {
      setHits(null);
      return;
    }
    timer.current = setTimeout(async () => {
      const query = text.trim();
      if (query.includes('@')) {
        // an email means exact lookup — never a directory scan
        const r = await fetch(`/api/users/lookup?email=${encodeURIComponent(query)}`);
        const d = r.ok ? await r.json() : {};
        setHits(d.found ? [{ ...d.found, friendship: d.found.friendship ?? 'none' }] : d.invite_hint ? 'invite' : []);
        return;
      }
      const r = await fetch(`/api/users/search?q=${encodeURIComponent(query)}`);
      if (r.ok) setHits((await r.json()).users ?? []);
    }, 250);
  };

  const add = async (username) => {
    const r = await fetch('/api/friends/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    const d = await r.json().catch(() => ({}));
    onToast?.(r.ok ? `friend card sent to ${username} ♥` : (d.error ?? 'could not send'));
    if (r.ok) {
      setHits((h) => h?.map((u) => (u.username === username ? { ...u, friendship: 'pending' } : u)));
      reloadSocial?.();
    }
  };

  return html`<div class="section">
    <div class="sectionhead">find people</div>
    <input type="text" class="searchbox" placeholder="username or buddy name…" value=${q}
      onInput=${(e) => { setQ(e.target.value); search(e.target.value); }} />
    ${hits === 'invite' &&
    html`<div class="dim" style="margin-top:6px">
      nobody here with that email yet —
      <span class="mini-link" onClick=${() => mintInvite(onToast)}>send them an invite (+5 seeds each)</span>
    </div>`}
    ${Array.isArray(hits) && hits.length === 0 && html`<div class="dim" style="margin-top:6px">
      nobody by that name yet · <span class="mini-link" onClick=${() => mintInvite(onToast)}>invite a friend</span>
    </div>`}
    ${Array.isArray(hits) && hits.map(
      (u) => html`<div class="friendrow">
        <${Pet} username=${u.username} cell=${3} />
        <div class="buddytxt clickable" onClick=${() => onProfile(u.username)}>
          <div class="convname">${u.username}${u.bot && html` <span class="botpill">bot</span>`} <span class="dot ${u.status?.state ?? 'idle'}"></span></div>
          ${u.pet_name && html`<div class="fstatus">with ${u.pet_name}</div>`}
        </div>
        ${u.friendship === 'none' && html`<button class="mini primary" onClick=${() => add(u.username)}>+ add</button>`}
        ${u.friendship === 'pending' && html`<span class="dim">sent ✓</span>`}
        ${u.friendship === 'accepted' && html`<span class="dim">buddies ♥</span>`}
      </div>`
    )}
  </div>`;
}

// the buddies board: who's around, what their agents are up to, one-tap kindnesses
// (tickets moved to the chats tab — DM composer 🎫 is the only send entry now)
export function BuddiesTab({ social, me, roster = [], onProfile, onOpenDm, reloadSocial, onToast }) {
  const friends = social?.friends ?? [];
  const requests_in = social?.requests_in ?? [];

  const accept = async (username) => {
    await fetch('/api/friends/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    reloadSocial();
  };

  // kindnesses moved into the garden itself — visiting is the verb now
  const [visiting, setVisiting] = useState(null);

  // roster entries may be plain strings or { username, bot } (ws presence contract) — take both
  const here = roster
    .map((r) => (typeof r === 'string' ? r : r?.username))
    .filter((n) => n && n !== me.username);

  return html`<div class="metab">
    <div class="invitecard"
      title="one link = one friend (fresh codes, 20/day) · they open the link, then sign in from that same browser · install: npx vibebuddy app (or the porch download buttons)">
      <div>bring a vibe coding friend — you both get +5 seeds when their agent first connects</div>
      <button class="mini primary" onClick=${() => mintInvite(onToast)}>🎟 mint an invite link</button>
      <div class="dim">one link = one friend · they open it in the browser they'll sign in with</div>
    </div>

    ${here.length > 0 &&
    html`<div class="section">
      <div class="sectionhead">around right now</div>
      <div class="rosterrow">
        ${here.map(
          (n) => html`<span class="rosterface clickable" title=${n} onClick=${() => onProfile(n)}>
            <${Pet} username=${n} cell=${2} />
            <span class="rostername">${n}</span>
          </span>`
        )}
      </div>
    </div>`}

    <${UserSearch} onProfile=${onProfile} reloadSocial=${reloadSocial} onToast=${onToast} />

    ${requests_in.length > 0 &&
    html`<div class="section">
      <div class="sectionhead">friend requests</div>
      ${requests_in.map(
        (r) => html`<div class="friendrow">
          <${Pet} username=${r.user.username} cell=${3} />
          <span class="fname clickable" onClick=${() => onProfile(r.user.username)}>${r.user.username}</span>
          <button class="mini primary" onClick=${() => accept(r.user.username)}>accept</button>
        </div>`
      )}
    </div>`}

    <div class="section">
      <div class="sectionhead">buddies <span class="dim">· ${friends.length}</span></div>
      ${friends.length === 0 &&
      html`<div class="dim">none yet — say hi in the club lobby or swap cards at a table.</div>`}
      ${friends.map(
        (f) => html`<div class="buddyrow">
          <span class="clickable" onClick=${() => onProfile(f.user.username)}>
            <${Pet} username=${f.user.username} cell=${3} />
          </span>
          <div class="buddytxt clickable" onClick=${() => onProfile(f.user.username)}>
            <div class="convname">
              ${f.user.username}
              ${(f.status?.kinds?.length ?? 0) === 0 && html`<span class="dot ${f.status?.state ?? 'idle'}"></span>`}
              ${(f.status?.kinds ?? []).map(
                (k) => html`<span class="kindchip withdot"><span class="dot ${k.state}"></span>${agentLabel(k.kind)}</span>`
              )}
            </div>
            <div class="fstatus">${funStatusLine(f.status)}</div>
          </div>
          <span class="buddyacts">
            <button class="mini" title="message" onClick=${() => onOpenDm(f.user.username)}>💬</button>
            <button class="mini" title="visit their garden — water, butterflies, pats live there"
              onClick=${() => setVisiting(f.user.username)}>🌱</button>
          </span>
        </div>`
      )}
    </div>
    ${visiting && html`<${GardenPage} me=${me} username=${visiting} onToast=${onToast} onClose=${() => setVisiting(null)} />`}
  </div>`;
}


export function ProfileView({ username, me, onClose, onOpenDm, reloadSocial }) {
  const [data, setData] = useState(null);
  const [gardenOpen, setGardenOpen] = useState(false);
  const load = () =>
    fetch(`/api/profile/${username}`)
      .then((r) => r.json())
      .then(setData);
  useEffect(() => {
    load();
  }, [username]);

  const act = async (path, body) => {
    await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    load();
    reloadSocial();
  };

  if (!data) return html`<div class="overlay"><div class="empty">…</div></div>`;
  const isSelf = me.username === data.user?.username;
  const PlantMini = ({ p }) => {
    const ref = useRef(null);
    useEffect(() => {
      if (ref.current) drawPlant(ref.current, p.seed, p.kind, { cell: 4 });
    }, [p.seed]);
    return html`<canvas ref=${ref} class="pixel" width="32" height="32" style="width:32px;height:32px" />`;
  };

  return html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && onClose()}>
    <div class="profilecard">
      <div class="profiletop">
        <${Pet} username=${data.user.username} cell=${5} stage=${data.pet?.stage ?? 'adult'} wins=${data.pet?.wins ?? 0} />
        <div>
          <div class="deskname">
            ${data.user.username}
            ${data.user.bot && html` <span class="botpill">bot</span>`}
            ${data.user.github_login &&
            html` <a href=${`https://github.com/${data.user.github_login}`} target="_blank" rel="noreferrer"
              title="${data.user.github_login} on github">🐙</a>`}
          </div>
          <div class="desksub">
            <span class="dot ${data.status?.state ?? 'idle'}"></span> ${funStatusLine(data.status)}
          </div>
          ${data.pet &&
          html`<div class="desksub">
            ${data.user.pet_name ?? 'buddy'} · lv.${data.pet.level} ${data.pet.stage} · feeling ${data.pet.happiness}
          </div>`}
          ${data.agents?.length > 0 &&
          html`<div class="desksub agentline">
            runs
            ${data.agents.map((a) => {
              const live = (data.status?.kinds ?? []).find((k) => k.kind === a.kind);
              return html`<span class="kindchip withdot">
                <span class="dot ${live?.state ?? 'idle'}"></span>${agentLabel(a.kind)}${a.hours ? ` ${a.hours}h` : ''}${a.ships ? ` · ⛵${a.ships}` : ''}
              </span>`;
            })}
          </div>`}
          ${data.ship_count > 0 && html`<div class="desksub">⛵ ${data.ship_count} ships</div>`}
        </div>
        <span class="closex clickable" onClick=${onClose}>✕</span>
      </div>

      ${!isSelf &&
      html`<div class="profileactions">
        <button class="mini primary" onClick=${() => onOpenDm(username)}>💬 message</button>
        <button class="mini" onClick=${() => setGardenOpen(true)}>🌱 visit garden</button>
        <button
          class="mini danger"
          onClick=${() => confirm(`block ${username}? you'll never see them again.`) && act('/api/block', { username })}
        >
          🚫 block
        </button>
        <button
          class="mini"
          onClick=${() => {
            const why = prompt('report — what happened?');
            if (why) act('/api/report', { username, context: 'profile', body: why });
          }}
        >
          ⚑ report
        </button>
      </div>
      ${data.friendship === 'none' &&
      !data.blocked &&
      html`<div class="dim" style="margin:-6px 0 12px">
        not buddies yet —${' '}
        <span class="mini-link" style="float:none" onClick=${() => act('/api/friends/request', { username })}>
          send a buddy card ♥
        </span>
      </div>`}
      ${data.friendship === 'pending' && html`<div class="dim" style="margin:-6px 0 12px">buddy card sent ✓</div>`}`}

      ${data.ships &&
      html`<div class="section">
        <div class="sectionhead">recent ships</div>
        ${data.ships.slice(0, 5).map(
          (s) => html`<div class="shiprow">
            <span class="shiptitle">${s.title}</span>
            ${s.adds != null && html`<span class="adds">+${s.adds}</span>`}
            ${s.dels != null && html`<span class="dels">−${s.dels}</span>`}
          </div>`
        )}
      </div>`}
      ${data.garden &&
      html`<div class="section">
        <div class="sectionhead">
          garden
          <span class="mini-link" onClick=${() => setGardenOpen(true)}>🌿 wander in</span>
        </div>
        <div class="gardengrid">${data.garden.map((p) => html`<${PlantMini} p=${p} />`)}</div>
      </div>`}
      ${!data.ships && !data.garden && !isSelf && html`<div class="dim">this desk keeps its stats private.</div>`}
    </div>
    ${gardenOpen &&
    html`<${GardenPage} me=${me} username=${data.user.username} onClose=${() => setGardenOpen(false)} />`}
  </div>`;
}

// ---- tickets ----

// read a zip's central directory in the browser — listing is sender-declared, never verified
export async function zipListing(file) {
  const tail = await file.slice(Math.max(0, file.size - 66_000)).arrayBuffer();
  const dv = new DataView(tail);
  let eocd = -1;
  for (let i = dv.byteLength - 22; i >= 0; i--)
    if (dv.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  if (eocd === -1) return null;
  const count = dv.getUint16(eocd + 10, true);
  const cdOffset = dv.getUint32(eocd + 16, true);
  const cd = new DataView(await file.slice(cdOffset).arrayBuffer());
  const names = [];
  let p = 0;
  const dec = new TextDecoder();
  for (let i = 0; i < count && p + 46 <= cd.byteLength; i++) {
    if (cd.getUint32(p, true) !== 0x02014b50) break;
    const size = cd.getUint32(p + 24, true);
    const nameLen = cd.getUint16(p + 28, true);
    const extraLen = cd.getUint16(p + 30, true);
    const commentLen = cd.getUint16(p + 32, true);
    names.push({ name: dec.decode(new Uint8Array(cd.buffer, p + 46, nameLen)), size });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return names;
}

const RISK_TEXT =
  'VibeBuddy does not inspect ticket contents. Running someone else’s code is at your own risk — use a container or sandbox.';

export function SendTicketForm({ toUsername, onDone }) {
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [fileMeta, setFileMeta] = useState(null); // {file, listing}
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const listing = await zipListing(f);
    if (!listing) return setErr('that does not look like a zip');
    setErr('');
    setFileMeta({ file: f, listing });
  };

  const send = async () => {
    if (!title.trim()) return setErr('give it a title');
    setBusy(true);
    setErr('');
    let filePart = {};
    if (fileMeta) {
      // the declared type is signed into the PUT url — the wire header must match it
      // byte-for-byte (Windows reports .zip as x-zip-compressed, empty types drop the header)
      const zipType = 'application/zip';
      const meta = await fetch('/api/uploads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: fileMeta.file.name, size: fileMeta.file.size, type: zipType }),
      });
      if (!meta.ok) {
        setBusy(false);
        return setErr((await meta.json()).error ?? 'upload refused');
      }
      const { upload_url, file_url } = await meta.json();
      const put = await fetch(upload_url, { method: 'PUT', headers: { 'content-type': zipType }, body: fileMeta.file });
      if (!put.ok) {
        setBusy(false);
        return setErr('upload failed');
      }
      filePart = {
        file_url,
        file_name: fileMeta.file.name,
        file_size: fileMeta.file.size,
        listing: fileMeta.listing,
      };
    }
    const r = await fetch('/api/tickets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: toUsername, title, brief, ...filePart }),
    });
    setBusy(false);
    if (r.ok) onDone();
    else setErr((await r.json()).error ?? 'failed');
  };

  return html`<div class="groupform ticketform">
    <input type="text" placeholder="ticket title (what needs doing)" value=${title} onInput=${(e) => setTitle(e.target.value)} />
    <input type="text" placeholder="short brief" value=${brief} onInput=${(e) => setBrief(e.target.value)} />
    <input type="file" ref=${fileRef} style="display:none" accept="application/zip,.zip" onChange=${pick} />
    <div class="pillrow">
      <button class="mini" onClick=${() => fileRef.current?.click()}>
        ${fileMeta ? `📦 ${fileMeta.file.name} (${fileMeta.listing.length} files)` : '📦 attach zip'}
      </button>
      <button class="mini primary" disabled=${busy} onClick=${send}>${busy ? '…' : 'send ticket'}</button>
    </div>
    ${err && html`<div class="formerr" style="padding:0">${err}</div>`}
  </div>`;
}

export function TicketsSection({ me, ticketsVersion }) {
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [acked, setAcked] = useState(false);
  const [note, setNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const load = () => fetch('/api/tickets').then((r) => r.json()).then((d) => setTickets(d.tickets ?? []));
  useEffect(load, [ticketsVersion]);

  const act = async (id, action, body = {}) => {
    await fetch(`/api/tickets/${id}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setAcked(false);
    setNote('');
    load();
  };

  const all = tickets ?? [];
  const needsYou = all.filter((t) => t.to === me.username && t.status === 'sent');
  const inProgress = all.filter((t) => t.to === me.username && t.status === 'accepted');
  const waiting = all.filter((t) => t.from === me.username && ['sent', 'accepted'].includes(t.status));
  const history = all.filter((t) => ['returned', 'declined', 'cancelled'].includes(t.status));
  const open = all.find((t) => t.id === openId);

  const row = (t, extra = '') => html`<div class="convrow clickable" onClick=${() => setOpenId(openId === t.id ? null : t.id)}>
    <span class="gameglyph">🎫</span>
    <div class="convtxt">
      <div class="convname">${t.title} <span class="statuschip s-${t.status}">${t.status}</span></div>
      <div class="convlast">${t.from === me.username ? `to ${t.to}` : `from ${t.from}`}${extra}</div>
    </div>
    ${t.from === me.username && t.status === 'sent' &&
    html`<button class="mini" onClick=${(e) => { e.stopPropagation(); act(t.id, 'cancel'); }}>withdraw</button>`}
  </div>`;

  return html`<div class="section">
    <div class="sectionhead">tickets <span class="dim">· work handoffs between friends</span></div>
    ${all.length === 0 &&
    html`<div class="dim">none yet — open a DM with a buddy and tap 🎫 to hand off a task + zip.</div>`}
    ${needsYou.length > 0 && html`<div class="dim" style="margin:4px 0">needs you:</div>`}
    ${needsYou.map((t) => row(t))}
    ${inProgress.length > 0 && html`<div class="dim" style="margin:4px 0">on your desk:</div>`}
    ${inProgress.map((t) => row(t))}
    ${waiting.length > 0 && html`<div class="dim" style="margin:4px 0">waiting on them:</div>`}
    ${waiting.map((t) => row(t))}
    ${history.length > 0 &&
    html`<div class="mini-link" style="float:none;margin-top:6px" onClick=${() => setShowHistory(!showHistory)}>
      ${showHistory ? 'hide' : 'show'} history (${history.length})
    </div>`}
    ${showHistory && history.map((t) => row(t))}
    ${open &&
    html`<div class="ticketcard">
      ${open.brief && html`<p class="dim">${open.brief}</p>`}
      ${open.listing &&
      html`<div class="ziplist">
        <div class="dim" style="margin-bottom:4px">file list <i>(declared by sender, unverified)</i>:</div>
        ${open.listing.slice(0, 12).map((f) => html`<div class="zipentry">${f.name} <span>${f.size ?? '?'} B</span></div>`)}
        ${open.listing.length > 12 && html`<div class="dim">…and ${open.listing.length - 12} more</div>`}
      </div>`}

      ${open.to === me.username &&
      open.status === 'sent' &&
      html`
        <div class="riskbox">⚠ ${RISK_TEXT}</div>
        <label class="pick" style="margin:8px 0">
          <input type="checkbox" checked=${acked} onChange=${(e) => setAcked(e.target.checked)} />
          I understand and accept the risk
        </label>
        <div class="pillrow">
          <button class="mini primary" disabled=${!acked} onClick=${() => act(open.id, 'accept', { acknowledge_risk: true })}>
            accept
          </button>
          <button class="mini" onClick=${() => act(open.id, 'decline')}>decline</button>
        </div>
      `}
      ${open.to === me.username &&
      open.status === 'accepted' &&
      html`
        ${open.file_url && html`<a class="filelink" href=${open.file_url}>⬇ download ${open.file_name}</a>`}
        <input type="text" placeholder="result note…" value=${note} onInput=${(e) => setNote(e.target.value)} style="margin:8px 0" />
        <button class="mini primary" onClick=${() => act(open.id, 'return', { note })}>return ticket</button>
      `}
      ${open.status === 'returned' &&
      html`<div class="dim">returned${open.result_note ? `: ${open.result_note}` : ''}</div>
        ${open.result_file_url && html`<a class="filelink" href=${open.result_file_url}>⬇ ${open.result_file_name}</a>`}`}
    </div>`}
  </div>`;
}

export function TableView({ ws, table, tableMsgs, queued, onProfile, me }) {
  const [draft, setDraft] = useState('');
  const [, setTick] = useState(0);
  const feedRef = useRef(null);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 3000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [tableMsgs]);

  if (queued)
    return html`<div class="empty">
      <div class="candlewrap"><div class="candleflame">🕯</div></div>
      <div>finding you a table…</div>
      ${queued.size &&
      html`<div class="dim">${queued.waiting ?? 1} of ${queued.size} here — waiting for more folks</div>`}
      <button class="mini" onClick=${() => ws?.send(JSON.stringify({ t: 'table_leave' }))}>never mind</button>
    </div>`;

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    ws?.send(JSON.stringify({ t: 'table_send', body }));
    setDraft('');
  };

  return html`
    <div class="roomhead tablehead">
      <span>table for ${table.size}</span>
      <span class="members">
        ${table.members.map(
          (m) => html`<span class="clickable" onClick=${() => onProfile(m.username)}>
            <${Pet} username=${m.username} cell=${3} />
          </span>`
        )}
      </span>
      <span class="mini-link" onClick=${() => ws?.send(JSON.stringify({ t: 'table_leave' }))}>leave</span>
    </div>
    <div class="icebreaker">${table.icebreaker}</div>
    <div class="feed" ref=${feedRef}>
      ${tableMsgs.map((m) => html`<${MsgRow} m=${m} onProfile=${onProfile} />`)}
      ${tableMsgs.length === 0 && html`<div class="empty"><div>you're seated — say hi</div></div>`}
    </div>
    <div class="tableactions">
      ${table.members
        .filter((m) => m.username !== me.username)
        .map(
          (m) => html`<button class="mini" onClick=${() => ws?.send(JSON.stringify({ t: 'table_card', to: m.username }))}>
            🤝 ${m.username}
          </button>`
        )}
    </div>
    <div class="composer">
      <input
        type="text"
        maxlength="200"
        placeholder="table talk…"
        value=${draft}
        onInput=${(e) => setDraft(e.target.value)}
        onKeyDown=${(e) => e.key === 'Enter' && send()}
      />
      <button onClick=${send}>send</button>
    </div>
  `;
}
